import { isEmpty, omit, isNull } from 'lodash';
import isNil from 'lodash/isNil';
import isUndefined from 'lodash/isUndefined';
import { ISavedFilter } from '../../../../../components/sidebar-filters/saved-filter/interface';
import { PjmRegistrationFilterKeys, TPjmRegistrationFilterKey } from './PjmRegistrationSidebarFilters';
import { ISidebarFilterOptionsContext } from '../../../../energyUsageData/sidebar/context/SidebarFilterOptionsContext';

export const transformPjmRegistrationsSavedFilterToUser = (
    filter: ISavedFilter['filter_values'],
    context: ISidebarFilterOptionsContext
): Record<TPjmRegistrationFilterKey, string> => {
    let result: Record<TPjmRegistrationFilterKey, string> = {} as Record<TPjmRegistrationFilterKey, string>;

    for (const [key, value] of Object.entries(filter)) {
        // Filter out deselected values to avoid displaying them as "unknown"
        if (isUndefined(value) || isNull(value)) continue;

        switch (key) {
            case PjmRegistrationFilterKeys.status:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmRegistrationStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmRegistrationFilterKeys.zone:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmRegistrationZoneOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmRegistrationFilterKeys.edc:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmRegistrationEdcOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmRegistrationFilterKeys.resourceType:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmRegistrationResourceTypeOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmRegistrationFilterKeys.product:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmRegistrationProductOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmRegistrationFilterKeys.leadTime:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmRegistrationLeadTimeOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmRegistrationFilterKeys.completed:
                result[key] = context.pjmRegistrationCompletedOptions.find(option => option.value === value)?.label || 'unknown';
                break;
            case PjmRegistrationFilterKeys.underDuplicateReview:
                result[key] = context.pjmRegistrationUnderDuplicateReviewOptions.find(option => option.value === value)?.label || 'unknown';
                break;
            case PjmRegistrationFilterKeys.syncDrhubStatus:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmRegistrationSyncDrhubStatusOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            case PjmRegistrationFilterKeys.season:
                result[key] = value
                    .map((val: string) => {
                        const option = context.pjmRegistrationSeasonOptions.find(option => option.value === val);

                        return option?.label || 'unknown';
                    })
                    .join(', ');
                break;
            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    // Remove empty properties from the result object
    const emptyProperties = getEmptyFilterProperties(result);
    if (emptyProperties.length > 0) {
        result = omit(result, emptyProperties) as Record<TPjmRegistrationFilterKey, string>;
    }

    return result;
};

export const transformPjmRegistrationsSavedFilterValuesFromDto = (
    filter: ISavedFilter['filter_values']
): Record<TPjmRegistrationFilterKey, unknown> => {
    const result: Record<TPjmRegistrationFilterKey, unknown> = {} as Record<TPjmRegistrationFilterKey, unknown>;

    // Convert values from DTO format to interface format
    for (const [key, value] of Object.entries(filter)) {
        if (isUndefined(value)) continue;

        switch (key) {
            case PjmRegistrationFilterKeys.status:
            case PjmRegistrationFilterKeys.zone:
            case PjmRegistrationFilterKeys.edc:
            case PjmRegistrationFilterKeys.resourceType:
            case PjmRegistrationFilterKeys.product:
            case PjmRegistrationFilterKeys.leadTime:
            case PjmRegistrationFilterKeys.completed:
            case PjmRegistrationFilterKeys.underDuplicateReview:
            case PjmRegistrationFilterKeys.syncDrhubStatus:
            case PjmRegistrationFilterKeys.season:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = value; // Keep other values as is
        }
    }

    return result;
};

export const transformPjmRegistrationsSavedFilterValuesToDto = (
    filter: ISavedFilter['filter_values']
): Record<TPjmRegistrationFilterKey, unknown> => {
    const result: Record<TPjmRegistrationFilterKey, unknown> = {} as Record<TPjmRegistrationFilterKey, unknown>;

    for (const [key, value] of Object.entries(filter)) {
        switch (key) {
            case PjmRegistrationFilterKeys.status:
            case PjmRegistrationFilterKeys.zone:
            case PjmRegistrationFilterKeys.edc:
            case PjmRegistrationFilterKeys.resourceType:
            case PjmRegistrationFilterKeys.product:
            case PjmRegistrationFilterKeys.leadTime:
            case PjmRegistrationFilterKeys.completed:
            case PjmRegistrationFilterKeys.underDuplicateReview:
            case PjmRegistrationFilterKeys.syncDrhubStatus:
            case PjmRegistrationFilterKeys.season:
                result[key] = value;
                break;

            default:
                // @ts-ignore
                result[key] = String(value);
        }
    }

    return result;
};

export const checkFilterInvalidValues = (filter: ISavedFilter['filter_values'], context: ISidebarFilterOptionsContext) => {
    const invalidFields: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        if (isNil(value) || isEmpty(value)) continue;

        switch (key) {
            case PjmRegistrationFilterKeys.status:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmRegistrationStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmRegistrationFilterKeys.zone:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmRegistrationZoneOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmRegistrationFilterKeys.edc:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmRegistrationEdcOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmRegistrationFilterKeys.resourceType:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmRegistrationResourceTypeOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmRegistrationFilterKeys.product:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmRegistrationProductOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmRegistrationFilterKeys.leadTime:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmRegistrationLeadTimeOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmRegistrationFilterKeys.completed:
                if (typeof value === 'string' && !context.pjmRegistrationCompletedOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;
            case PjmRegistrationFilterKeys.underDuplicateReview:
                if (typeof value === 'string' && !context.pjmRegistrationUnderDuplicateReviewOptions.some(opt => opt.value === value)) {
                    invalidFields.push(key);
                }
                break;
            case PjmRegistrationFilterKeys.syncDrhubStatus:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmRegistrationSyncDrhubStatusOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
            case PjmRegistrationFilterKeys.season:
                if (Array.isArray(value)) {
                    const validOptionValues = context.pjmRegistrationSeasonOptions.map(opt => opt.value);
                    if (value.some(id => !validOptionValues.includes(id))) {
                        invalidFields.push(key);
                    }
                }
                break;
        }
    }

    return invalidFields;
};

export function countNotEmptyFilters(filters: Record<string, any>): number {
    if (!filters) return 0;

    return Object.values(filters).filter(value => {
        if (Array.isArray(value)) return value.length > 0;

        if (typeof value === 'number') return !isNaN(value);

        if (typeof value === 'string') return value.trim() !== '';

        if (typeof value === 'boolean') return true;

        return false;
    }).length;
}

function getEmptyFilterProperties(obj: Record<TPjmRegistrationFilterKey, string>): TPjmRegistrationFilterKey[] {
    return Object.entries(obj)
        .filter(([_, value]) => isEmpty(value))
        .map(([key, _]) => key) as TPjmRegistrationFilterKey[];
}
